import { useMemo, useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { GoGoal, GoShieldCheck, GoAlert, GoFileBinary, GoX } from "react-icons/go";
import ScanMetricCards from "../../components/Scan/ScanMetricCards";
import ScanCharts from "../../components/Scan/ScanCharts";
import ScanRatingChart from "../../components/Scan/ScanRatingChart";
import ScanHorizontalBarChart from "../../components/Scan/ScanHorizontalBarChart";
import { getScanMetrics } from "./scanMetrics";

const SEVERITY_PALETTE = {
    critical: "#e63950",
    high: "#ff6b6b",
    medium: "#f5b942",
    low: "#f9e79b",
    info: "#8e44ad",
};

const CATEGORY_PALETTE = [
    "#8295ec", "#ff6b6b", "#f5b942", "#2ecc71", "#8e44ad", "#4ac2c9", "#e67e22",
];

function buildAffectedResourceData(findings) {
    if (!Array.isArray(findings)) {
        return { affectedResourceData: [], affectedResourceCount: null };
    }

    const levels = ["critical", "high", "medium", "low"];
    const highestSeverityByUrl = new Map();

    for (const finding of findings) {
        const rank = levels.indexOf(finding?.severity);
        if (rank === -1) continue;
        const target = finding?.target;
        if (typeof target !== "string") continue;

        try {
            const url = new URL(target);
            if (url.protocol !== "http:" && url.protocol !== "https:") continue;
        } catch { continue; }

        const previousRank = highestSeverityByUrl.get(target);
        if (previousRank === undefined || rank < previousRank) {
            highestSeverityByUrl.set(target, rank);
        }
    }

    const countsBySeverity = levels.map(() => 0);
    for (const rank of highestSeverityByUrl.values()) {
        countsBySeverity[rank] += 1;
    }

    const total = highestSeverityByUrl.size;
    const data = levels
        .map((severity, index) => ({
            name: severity.charAt(0).toUpperCase() + severity.slice(1),
            value: countsBySeverity[index],
            percent: total ? Number(((countsBySeverity[index] / total) * 100).toFixed(1)) : 0,
            color: SEVERITY_PALETTE[severity],
        }))
        .filter((item) => item.value > 0);

    return { affectedResourceData: data, affectedResourceCount: total };
}

export default function ScanReport({ report: dataJsonReport, onViewReport: openReport, reportLoading = false }) {
    const { t } = useTranslation();
    const [popupData, setPopupData] = useState(null);

    // 1. Empêcher le scroll du body quand la popup est ouverte
    useEffect(() => {
        if (popupData) {
            document.body.style.overflow = "hidden";
        } else {
            document.body.style.overflow = "";
        }
        return () => {
            document.body.style.overflow = "";
        };
    }, [popupData]);

    const {
        counts, score, totalFindings, criticalHighCount, priorityCount, targetContext,
        maintenance, severityData, categoryData, vulnerabilityData, sourceData,
        affectedResourceData, affectedResourceCount, findingsList
    } = useMemo(() => {
        const metrics = getScanMetrics(dataJsonReport);
        const { counts: c, findings, totalFindings: total } = metrics;

        const severity = Object.entries(c)
            .filter(([, value]) => value > 0)
            .map(([key, value]) => ({
                name: key.charAt(0).toUpperCase() + key.slice(1),
                value,
                percent: total ? Math.round((value / total) * 100) : 0,
                color: SEVERITY_PALETTE[key],
                rawKey: key,
            }));

        const catCounts = Object.create(null);
        findings.forEach((finding) => {
            const category = finding.category || "Other";
            catCounts[category] = (catCounts[category] || 0) + 1;
        });

        const category = Object.entries(catCounts)
            .sort((a, b) => b[1] - a[1])
            .map(([name, value], index) => ({
                name,
                value,
                percent: total ? Math.round((value / total) * 100) : 0,
                color: CATEGORY_PALETTE[index % CATEGORY_PALETTE.length],
            }));

        const { affectedResourceData, affectedResourceCount } = buildAffectedResourceData(Array.isArray(dataJsonReport.findings) ? findings : null);

        const vulnerabilityCounts = Object.create(null);
        findings.forEach((finding) => {
            const title = finding.title || "Unknown";
            vulnerabilityCounts[title] = (vulnerabilityCounts[title] || 0) + 1;
        });

        const vulnerabilities = Object.entries(vulnerabilityCounts)
            .sort((a, b) => b[1] - a[1])
            .slice(0, 8)
            .map(([name, value]) => ({ name, value }));

        const sourceCounts = Object.create(null);
        findings.forEach((finding) => {
            const source = finding.source || "Unknown";
            sourceCounts[source] = (sourceCounts[source] || 0) + 1;
        });

        const sources = Object.entries(sourceCounts)
            .sort((a, b) => b[1] - a[1])
            .map(([name, value]) => ({ name, value }));

        return {
            ...metrics, counts: c, score: dataJsonReport.summary?.score ?? "—",
            totalFindings: total, severityData: severity, categoryData: category,
            vulnerabilityData: vulnerabilities, sourceData: sources,
            affectedResourceData, affectedResourceCount, findingsList: findings || []
        };
    }, [dataJsonReport]);

    const handleChartClick = (type, clickedItemName) => {
        if (!type) return;
        
        let filteredFindings = [];
        
        switch(type) {
            case 'all':
                filteredFindings = findingsList;
                break;
            case 'severity':
                filteredFindings = findingsList.filter(f => f.severity.toLowerCase() === clickedItemName.toLowerCase());
                break;
            case 'category':
                filteredFindings = findingsList.filter(f => f.category === clickedItemName);
                break;
            case 'vulnerability':
                filteredFindings = findingsList.filter(f => f.title === clickedItemName);
                break;
            case 'source':
                filteredFindings = findingsList.filter(f => f.source === clickedItemName);
                break;
                
            case 'affected_resource': {
                const levels = ["critical", "high", "medium", "low"];
                const highestSeverityByUrl = new Map();

                for (const finding of findingsList) {
                    const rank = levels.indexOf(finding?.severity);
                    if (rank === -1) continue;
                    
                    const target = finding?.target;
                    if (typeof target !== "string") continue;
                    
                    try {
                        const url = new URL(target);
                        if (url.protocol !== "http:" && url.protocol !== "https:") continue;
                    } catch { continue; }

                    const previousRank = highestSeverityByUrl.get(target)?.rank;
                    if (previousRank === undefined || rank < previousRank) {
                        highestSeverityByUrl.set(target, { rank, finding });
                    }
                }

                const clickedRank = levels.indexOf(clickedItemName.toLowerCase());
                for (const { rank, finding } of highestSeverityByUrl.values()) {
                    if (rank === clickedRank) {
                        filteredFindings.push(finding);
                    }
                }
                break;
            }
                
            default:
                break;
        }

        setPopupData({
            title: clickedItemName,
            type: type,
            items: filteredFindings
        });
    };

    const maintenanceLabel = maintenance.kind === "tls" ? "TLS Certificate" : "Outdated Components";
    const maintenanceValue = maintenance.kind === "tls"
        ? maintenance.value < 0 ? t("Expiré") : `${maintenance.value} ${t("jours")}`
        : maintenance.value ?? "—";
    const maintenanceInfo = maintenance.kind === "components"
        ? maintenance.value > 0
            ? [
                maintenance.plugins > 0 && `${maintenance.plugins} ${t("plugins")}`,
                maintenance.themes > 0 && `${maintenance.themes} ${t("thèmes")}`,
                maintenance.core > 0 && t("cœur WordPress"),
            ].filter(Boolean).join(" · ")
            : `${maintenance.assessed} ${t("composants évalués")}`
        : maintenance.kind === "tls"
            ? [maintenance.version, t("Échéance au moment du scan")].filter(Boolean).join(" · ")
            : t("Non évalué dans ce rapport");

    return (
        <div className="scan-page-content-body-content relative">
            <div className="scan-metric">
                <ScanMetricCards
                    icon={<GoGoal />}
                    value={totalFindings}
                    label="Total Findings"
                    trendValue={targetContext || t("Cible non renseignée")}
                    onClick={() => {
                        if (findingsList.length > 0) {
                            setPopupData({
                                title: "Total Findings",
                                type: "metric",
                                items: findingsList,
                            });
                        }
                    }}
                />
                <ScanMetricCards
                    icon={<GoAlert />}
                    value={criticalHighCount}
                    label="Critical & High"
                    trendValue={criticalHighCount === 0 ? <span className="scan-metric-success">{t("Aucun résultat critique ou élevé")}</span> : `${counts.critical} ${t("critical")} / ${counts.high} ${t("high")}`}
                    onClick={() => {
                        const filtered = findingsList.filter(f => f.severity === 'critical' || f.severity === 'high');
                        if (filtered.length > 0) {
                            setPopupData({ title: "Critical & High Findings", type: "metric", items: filtered });
                        }
                    }}
                />
                <ScanMetricCards icon={<GoFileBinary />} value={maintenanceValue} label={maintenanceLabel} trendValue={maintenanceInfo} />
                <ScanMetricCards
                    icon={<GoShieldCheck />}
                    value={priorityCount}
                    label="Priority Findings"
                    trendValue={`${counts.medium} medium + ${counts.high} high + ${counts.critical} critical`}
                    onClick={() => {
                        const prioritySeverities = ["critical", "high", "medium"];
                        const filtered = findingsList.filter(f => prioritySeverities.includes(f.severity?.toLowerCase()));
                        if (filtered.length > 0) {
                            setPopupData({
                                title: "Priority Findings",
                                type: "metric",
                                items: filtered,
                            });
                        }
                    }}
                />
            </div>

            <div className="scan-charts">
                <ScanCharts
                    title="Findings Severity"
                    subtitle="Distribution by severity"
                    data={severityData}
                    centerValue={totalFindings}
                    centerLabel="Total findings"
                    onViewReport={openReport}
                    reportLoading={reportLoading}
                    compact
                    onChartClick={(data) => handleChartClick('severity', data.name)}
                />

                <ScanCharts
                    title="Affected Resources"
                    subtitle="Highest severity per URL · Info excluded"
                    data={affectedResourceData}
                    centerValue={affectedResourceCount ?? "—"}
                    centerLabel="URLs with findings"
                    onViewReport={openReport}
                    reportLoading={reportLoading}
                    compact
                    onChartClick={(data) => handleChartClick('affected_resource', data.name)}
                />

                <ScanRatingChart
                    title="Cyber Security Rating"
                    subtitle="Higher score means better security"
                    score={score}
                    segments={severityData}
                    onViewReport={openReport}
                    reportLoading={reportLoading}
                    onChartClick={() => handleChartClick('all', 'Global Security Rating')}
                />
            </div>

            <div className="scan-section-title">
                <h2>{t("Scan statistics and findings")}</h2>
            </div>

            <div className="scan-charts scan-charts--bottom">
                <ScanHorizontalBarChart
                    title="Most common vulnerabilities"
                    subtitle="Occurrences by finding type"
                    data={vulnerabilityData}
                    onBarClick={(data) => handleChartClick('vulnerability', data.name)}
                />
                <ScanHorizontalBarChart
                    title="Findings by category"
                    subtitle="Distribution by category"
                    data={categoryData}
                    onBarClick={(data) => handleChartClick('category', data.name)}
                />
                <ScanHorizontalBarChart
                    title="Findings by source"
                    subtitle="Detected by scanning tool"
                    data={sourceData}
                    onBarClick={(data) => handleChartClick('source', data.name)}
                />
            </div>

            {popupData && (
                <div className="scan-popup-overlay" onClick={() => setPopupData(null)}>
                    <div className="scan-popup-square" onClick={(e) => e.stopPropagation()}>
                        <div className="scan-popup-header">
                            <h3>{t(popupData.title)} <span className="scan-popup-badge">{popupData.items.length}</span></h3>
                            <button className="scan-popup-close" onClick={() => setPopupData(null)}>
                                <GoX size={24} />
                            </button>
                        </div>
                        <div className="scan-popup-scroll-area">
                            {popupData.items.length === 0 ? (
                                <p className="scan-popup-empty">{t("Aucun détail supplémentaire.")}</p>
                            ) : (
                                popupData.items.map((item, i) => (
                                    <div key={i} className="scan-popup-item">
                                        <div className="scan-popup-item-header">
                                            <strong>{item.title}</strong>
                                            <span className={`severity-badge severity-${item.severity}`}>{item.severity}</span>
                                        </div>
                                        <p className="scan-popup-target">{item.target}</p>
                                        <p className="scan-popup-evidence">{item.evidence?.substring(0, 150)}...</p>
                                    </div>
                                ))
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}